// quiz_summary — end-of-lesson check: a small bank of single-answer questions.
// Submits the whole answer set once every question is answered. Server scores
// (correct / total) * 100.

import { useState, type ReactElement } from 'react';

import type { content } from '@fajrarabi/zod-schemas';

import { ArabicText, ChoiceButton } from './primitives';
import type { ActivityRendererProps } from './types';

type StrippedOption = { id: string; labelKey: string; image?: string | null };
type StrippedQuestion = { id: string; promptKey: string; options: StrippedOption[] };
type StrippedQuiz = { titleKey?: string | null; questions: StrippedQuestion[] };

export function QuizSummary({ data, ctx, cb }: ActivityRendererProps<StrippedQuiz>): ReactElement {
  const { t } = ctx;
  const [answers, setAnswers] = useState<Record<string, string>>({});

  function pick(questionId: string, chosenId: string): void {
    if (cb.locked) return;
    setAnswers((cur) => {
      const next = { ...cur, [questionId]: chosenId };
      if (Object.keys(next).length === data.questions.length) {
        cb.onAnswer({
          kind: 'quiz',
          answers: Object.entries(next).map(([qId, cId]) => ({
            questionId: qId,
            chosenId: cId,
          })),
        } satisfies content.AttemptInput);
      }
      return next;
    });
  }

  return (
    <div className="mx-auto flex max-w-md flex-col gap-7">
      <h2 className="text-center text-2xl font-bold text-burgundy-700">
        {t(data.titleKey ?? 'content.common.quiz_title')}
      </h2>

      {data.questions.map((q, qi) => (
        <div key={q.id}>
          <p className="mb-3 text-lg font-bold text-ink-900">
            <span className="me-2 text-ink-500">{qi + 1}.</span>
            <ArabicText text={t(q.promptKey)} ctx={ctx} />
          </p>
          <div className="grid gap-2">
            {q.options.map((opt) => (
              <ChoiceButton
                key={opt.id}
                onClick={() => pick(q.id, opt.id)}
                selected={answers[q.id] === opt.id}
                state="idle"
                disabled={cb.locked}
              >
                <ArabicText text={t(opt.labelKey)} ctx={ctx} />
              </ChoiceButton>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
